import { TZDate } from '@date-fns/tz';
import { format, isValid } from 'date-fns';

import type { IsoDateTime, LocalDate } from '@d3/contract';

import { EMPTY_PLACEHOLDER } from '@shared/lib/format';

/**
 * 날짜 · 시각 표시 정책
 * - 서버는 시각을 UTC ISO 문자열로 준다. 화면은 **서비스 고정 시간대(Asia/Seoul)** 로 표시한다.
 *   보는 사람의 브라우저 시간대를 따르지 않는다(같은 보고서가 사람마다 다른 날짜로 보이지 않게).
 * - 달력 날짜(`LocalDate`, `YYYY-MM-DD`)는 `Date` 로 바꾸지 않는다.
 * - `getHours()` 같은 암묵적 로컬 시간 함수를 쓰지 않는다.
 */
export const SERVICE_TIME_ZONE = 'Asia/Seoul';

export const DATE_PATTERN = {
  date: 'yyyy.MM.dd',
  dateTime: 'yyyy.MM.dd HH:mm',
  dateTimeSeconds: 'yyyy.MM.dd HH:mm:ss',
  time: 'HH:mm',
  localDate: 'yyyy-MM-dd',
} as const;

export type DatePattern = (typeof DATE_PATTERN)[keyof typeof DATE_PATTERN];

const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isLocalDate(value: unknown): value is LocalDate {
  if (typeof value !== 'string') return false;
  const match = LOCAL_DATE.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1) return false;
  // 달의 마지막 날 계산은 UTC 로 한다(브라우저 시간대와 무관).
  return day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** 시각(UTC ISO)을 서비스 시간대로 표시한다. */
export function formatDateTime(
  value: IsoDateTime | null | undefined,
  pattern: DatePattern = DATE_PATTERN.dateTime,
): string {
  if (!value) return EMPTY_PLACEHOLDER;
  const zoned = new TZDate(value, SERVICE_TIME_ZONE);
  return isValid(zoned) ? format(zoned, pattern) : EMPTY_PLACEHOLDER;
}

/** 달력 날짜를 표시한다. 시간대 변환을 하지 않는다. */
export function formatLocalDate(
  value: LocalDate | null | undefined,
  pattern: DatePattern = DATE_PATTERN.date,
): string {
  if (!value || !isLocalDate(value)) return EMPTY_PLACEHOLDER;
  const match = LOCAL_DATE.exec(value);
  if (!match) return EMPTY_PLACEHOLDER;
  // 서비스 시간대의 그 날 정오로 만든다 → 어느 시간대에서 실행해도 같은 날짜가 나온다.
  const zoned = new TZDate(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    12,
    SERVICE_TIME_ZONE,
  );
  return format(zoned, pattern);
}

/** 서비스 시간대 기준 "오늘". 입력 기본값에 쓴다. */
export function todayLocalDate(now: Date = new Date()): LocalDate {
  return format(new TZDate(now.getTime(), SERVICE_TIME_ZONE), DATE_PATTERN.localDate);
}
